import React from 'react';
import { CalendarDays, Hash, Info } from 'lucide-react';
import PublicTimeline from './PublicTimeline';
import PublicResultActions from './PublicResultActions';
import { useTenant } from '../../contexts/TenantContext';
import { receiptBrandFromTenant } from '../../utils/tenantBranding';
import {
  PROCESS_SITUACAO,
  PROCESS_TIPO,
  daysSince,
  formatPublicDate,
  processTimeline,
  statusMeta,
  toneClass,
} from '../../utils/publicStatus';

const NEXT = {
  1: 'Aguarde a distribuição. Se pediram documentos, entregue-os no mesmo protocolo.',
  2: 'Acompanhe exigências neste número. Respostas fora do prazo podem atrasar a análise.',
  3: 'Procure o setor de protocolo ou o portal do requerente para retirar o resultado.',
  4: 'O processo não segue em análise. Guarde o número caso precise reabrir o pedido.',
};

const PublicProcessResult = ({ row, orgName }) => {
  const { tenant } = useTenant();
  const sit = statusMeta(PROCESS_SITUACAO, row.situacao, row.situacao_label);
  const tipo = row.tipo_label || PROCESS_TIPO[Number(row.tipo)] || null;
  const opened = formatPublicDate(row.data_abertura);
  const elapsed = daysSince(row.data_abertura);
  const timeline = processTimeline(row.situacao);

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Protocolo do órgão</p>
          <p className="mt-0.5 flex items-center gap-2 font-mono text-lg font-bold tracking-tight text-slate-900">
            <Hash className="h-4 w-4 text-primary-700" aria-hidden />
            {row.numero}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${toneClass(sit.tone)}`}>
            {sit.label}
          </span>
          {tipo ? (
            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
              {tipo}
            </span>
          ) : null}
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 px-3 py-3">
            <dt className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden />
              Abertura
            </dt>
            <dd className="mt-1 text-sm font-semibold text-slate-900">{opened || 'Não informada'}</dd>
          </div>
          <div className="rounded-xl bg-slate-50 px-3 py-3">
            <dt className="text-xs font-medium text-slate-500">Tempo neste órgão</dt>
            <dd className="mt-1 text-sm font-semibold text-slate-900">
              {elapsed == null ? '—' : elapsed === 0 ? 'Registrado hoje' : `${elapsed} dia${elapsed === 1 ? '' : 's'}`}
            </dd>
          </div>
        </dl>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Linha do tempo pública</p>
          <PublicTimeline steps={timeline.steps} current={timeline.current} />
        </div>

        <div className="rounded-xl bg-primary-50/70 px-4 py-3 text-sm text-primary-900 ring-1 ring-primary-100">
          <p className="flex items-center gap-1.5 font-semibold">
            <Info className="h-4 w-4" aria-hidden />
            O que significa esta situação
          </p>
          <p className="mt-1 leading-relaxed text-primary-900/80">{sit.hint}</p>
          <p className="mt-2 font-medium text-primary-900">{NEXT[Number(row.situacao)] || NEXT[1]}</p>
        </div>

        <PublicResultActions
          protocol={row.numero}
          sharePath="/process-lookup"
          shareParam="numero"
          receipt={{
            ...receiptBrandFromTenant(tenant, orgName),
            title: 'Consulta pública de processo',
            protocol: row.numero,
            fields: [
              ['Situação', sit.label],
              ['Tipo', tipo],
              ['Abertura', opened],
              [
                'Tempo no órgão',
                elapsed == null ? null : elapsed === 0 ? 'Registrado hoje' : `${elapsed} dia(s)`,
              ],
            ],
            note: `${sit.hint} ${NEXT[Number(row.situacao)] || NEXT[1]}`,
          }}
        />
      </div>
    </article>
  );
};

export default PublicProcessResult;
